import { CloseOutlined, TagOutlined } from '@ant-design/icons';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import styled from 'styled-components';

import AIIcon from '@/components/Badge/AIBadge/AIIcon';
import { Divider, Flex, Space, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import Transition from '@/components/Common/Transition';
import { useModalContext } from '@/components/Modal/Modal';
import Heading from '@/components/Typography/Heading';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import { modalStyle } from '@/styles/style';
import { TOPIC_2_TYPE } from '@/types/taxonomyConst';

import CreateAiTopicContainer from './AiTopic/CreateAiTopicContainer';
import type { CandidateItemT } from './AiTopic/types';
import CreateManualTopicContainer from './ManualTopic/CreateManualTopicContainer';
import CreateRuleTopicContainer from './RuleTopic/CreateRuleTopicContainer';
import TopicTypeCard from './TopicTypeCard';
import type { CreateTopicModalProps } from './types';
import {
  type AiTopicCsvRow,
  parseAiTopicCsv,
  parseRuleTopicCsv,
  type RuleTopicCsvRow,
} from './utils/parseTopicCsv';

const SelectTopicTypeModal = <T extends CreateTopicTarget>(
  props: CreateTopicModalProps<T>,
) => {
  const color = useColor();
  const { closeModal } = useModalContext();

  const [type, setType] = useState<TOPIC_2_TYPE | null>(null);
  // CSV 업로드 데이터 상태
  const [aiTopicCsvData, setAiTopicCsvData] = useState<CandidateItemT[] | null>(
    null,
  );
  const [ruleTopicCsvData, setRuleTopicCsvData] = useState<
    Topic2CreateParams<TOPIC_2_TYPE.RULE>[] | null
  >(null);

  const isDisabled = (topicType: TOPIC_2_TYPE) => {
    if (props.type === null) return false;
    return props.type !== topicType;
  };

  // AI Topic CSV 업로드 핸들러
  const handleAiTopicCsvUpload = (data: Record<string, string>[]) => {
    const parsedData = parseAiTopicCsv(data as AiTopicCsvRow[]);
    if (parsedData.length > 0) {
      setAiTopicCsvData(parsedData);
      setType(TOPIC_2_TYPE.AI);
    }
  };

  // Rule Topic CSV 업로드 핸들러
  const handleRuleTopicCsvUpload = (data: Record<string, string>[]) => {
    const parsedData = parseRuleTopicCsv(data as RuleTopicCsvRow[]);
    if (parsedData.length > 0) {
      setRuleTopicCsvData(parsedData);
      setType(TOPIC_2_TYPE.RULE);
    }
  };

  return (
    <Transition
      status={!!type}
      initailInnerState={type ? 'inTran' : 'fallback'}
      fallback={
        <SelectTopicTypeStyle a="center">
          <VFlex g={14}>
            <Heading type="h2">Create your own topics</Heading>
            <Typo type="body2">
              Select your topic type to get started!
              <br />
              All topics on the same level must share the same topic type.
            </Typo>
          </VFlex>
          <Space h={70} />
          <Flex g={40}>
            <TopicTypeCard
              icon={<AIIcon className="text-xl" />}
              title="AI topics"
              desc="Effortlessly create topics using AI, along with taxonomy suggestions tailored to your feedback."
              onClick={() => setType(TOPIC_2_TYPE.AI)}
              disabled={isDisabled(TOPIC_2_TYPE.AI)}
              onCsvUpload={handleAiTopicCsvUpload}
              csvTemplateHref="/assets/taxonomy-ai-topic-csv.zip"
              csvTemplateFileName="taxonomy-ai-topic-csv.zip"
            />
            <TopicTypeCard
              icon={<TagOutlined style={{ fontSize: 22 }} />}
              title="Rule-based topics"
              desc="Structure your topics by adding keywords and tag-based rules to organize your taxonomy."
              onClick={() => setType(TOPIC_2_TYPE.RULE)}
              disabled={isDisabled(TOPIC_2_TYPE.RULE)}
              onCsvUpload={handleRuleTopicCsvUpload}
              csvTemplateHref="/assets/taxonomy-rule-topic-csv.zip"
              csvTemplateFileName="taxonomy-rule-topic-csv.zip"
            />
          </Flex>
          <Space h={24} />
          <Flex g={8} a="center" width="100%">
            <Divider style={{ flex: 1 }} />
            <Typo type="label2" color={color.gray600}>
              Or
            </Typo>
            <Divider style={{ flex: 1 }} />
          </Flex>
          <Space h={24} />
          <Button
            leftIcon={<Plus />}
            variant="ghost"
            sizeType="m"
            onClick={() => setType(TOPIC_2_TYPE.MANUAL)}
            disabled={isDisabled(TOPIC_2_TYPE.MANUAL)}
          >
            manual topics
          </Button>
          <IconButton
            className="btn-close"
            icon={<CloseOutlined />}
            sizeType="l"
            outlined={false}
            onClick={closeModal}
          />
        </SelectTopicTypeStyle>
      }
    >
      {type === TOPIC_2_TYPE.AI && (
        <CreateAiTopicContainer
          {...props}
          closeModal={closeModal}
          initialCsvData={aiTopicCsvData}
        />
      )}
      {type === TOPIC_2_TYPE.RULE && (
        <CreateRuleTopicContainer
          {...props}
          closeModal={closeModal}
          initialCsvData={ruleTopicCsvData}
        />
      )}
      {type === TOPIC_2_TYPE.MANUAL && (
        <CreateManualTopicContainer {...props} closeModal={closeModal} />
      )}
    </Transition>
  );
};

const SelectTopicTypeStyle = styled(VFlex)`
  position: relative;
  padding: 68px 180px;
  text-align: center;
  ${modalStyle};
  .btn-close {
    position: absolute;
    right: 24px;
    top: 24px;
  }
`;

export default SelectTopicTypeModal;
